import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useMemo } from 'react';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import useConciergeTiles from './useConciergeTiles';
import { useCachedRandom } from './useCachedRandom';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';
import { cloneDeep } from 'lodash-es';
import { useDefaultMediaMatchContext } from '@shared/utils/media/DefaultMediaMatchProvider';
import type { GalleryItemData } from './components/GalleryItem/GalleryItem';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { useValuePropsPayload } from './useValuePropsPayload';
import type { PropAsset, PropCarouselAsset } from './propValues.types';
import { isCarouselAsset } from './propValues.helpers';

const RESPONSIVE_CONFIG = {
  _: { row: 2, rowGap: 2 },
  sm2: { row: 3, rowGap: 2 },
  md3: { row: 4, rowGap: 1 },
  lg: { row: 5, rowGap: 1 },
  xl: { row: 6, rowGap: 1 },
};

export const useGalleryItemsWithValueProps = (items: ReadonlyArray<GalleryItemData>, isSearchActive: boolean) => {
  const { currentCategory: category } = useCardCategoryContext();
  const payload = useValuePropsPayload();
  const { getTotalActiveFilterCount } = useDesignGalleryFiltersContext();
  const injectConcierge = useConciergeTiles(category);
  const cachedRandom = useCachedRandom({ deps: [category] });
  const shuffleRandom = useCachedRandom({ deps: [category] });

  const isCategoryWithConciergeService = [StationeryTemplateCategoryEnum.invitation, StationeryTemplateCategoryEnum.saveTheDate].includes(category);
  const conciergeEnabled = isCategoryWithConciergeService;

  const defaultScreen = useDefaultMediaMatchContext()?.defaultScreen;

  // row - number of cards per row, rowGap - number of empty rows between cards
  const [rowConfig] = useResponsive(
    {
      values: RESPONSIVE_CONFIG,
    },
    defaultScreen === 'desktop' ? RESPONSIVE_CONFIG.lg : defaultScreen === 'tablet' ? RESPONSIVE_CONFIG.md3 : RESPONSIVE_CONFIG._,
  );

  const shouldInjectProps = getTotalActiveFilterCount() === 0 && !isSearchActive;

  const tiles = useMemo(() => {
    if (!shouldInjectProps) {
      for (const item of items) {
        // Allow the prop cards to flow during search, -1 signals no position.
        if (item.type === 'prop') {
          item.column = -1;
          item.row = -1;
        }
      }
      return items;
    }

    const templatesWithProps = [...items];

    let valueProps: (PropAsset | PropCarouselAsset)[] = [];

    const totalRows = items.length / (rowConfig?.row ?? 1);
    const requiredPropsToFillGallery = totalRows / ((rowConfig?.rowGap ?? 1) + 1);

    if (payload) {
      const originalValuePropsList = payload.props;

      valueProps = cloneDeep([...originalValuePropsList]);

      if (payload.repeatTiles) {
        const shuffledPool = [...originalValuePropsList].sort(a => {
          return shuffleRandom(originalValuePropsList.indexOf(a)) - 0.5;
        });

        if (valueProps.length < requiredPropsToFillGallery) {
          for (let i = valueProps.length; i < requiredPropsToFillGallery; i++) {
            const card = shuffledPool[i % originalValuePropsList.length];
            // Do not repeat the opening tile.
            if (!card.pinToStart) {
              valueProps.push({ ...card });
            }
          }
        }
      }
    }

    if (rowConfig) {
      let regularPropIndex = 0;
      // Make sure we do not place more props than can fit.
      const maxRows = (templatesWithProps.length + valueProps.length) / (rowConfig.row * (rowConfig.rowGap + 1)) - 1;

      valueProps.forEach((card, row) => {
        if (row >= maxRows) {
          return;
        }

        if (card.pinToStart) {
          card.row = 0;
          card.column = 0;
        } else {
          const availableColumns = Math.max(rowConfig.row - (card.columns ?? 1) + 1, 1);
          card.column = Math.floor(cachedRandom(row) * availableColumns);
          // Start regular props on visible row 4, after the carousel and price tile; rows are zero-based.
          card.row = 3 + (rowConfig.rowGap + 1) * regularPropIndex;
          regularPropIndex++;
        }
        templatesWithProps.push(isCarouselAsset(card) ? { ...card, type: 'propCarousel' } : { ...card, type: 'prop' });
      });
    }

    if (conciergeEnabled) {
      return injectConcierge(templatesWithProps, category);
    }

    return templatesWithProps;
  }, [items, shouldInjectProps, rowConfig, payload, category, conciergeEnabled, shuffleRandom, cachedRandom, injectConcierge]);

  return {
    itemsWithProps: tiles,
    galleryColumnCount: rowConfig.row,
  };
};
